import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSiteSettings } from '../../contexts/SiteSettingsContext';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext';
import { BasketIcon, BasketPopup } from '../Basket/Basket';
import { config } from '../../config';
import styles from './Navigation.module.css';

export const Navigation: React.FC = () => {
  const { t } = useTranslation();
  const { settings } = useSiteSettings();
  const { isAuthenticated, logout } = useCustomerAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [basketOpen, setBasketOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const basketRef = useRef<HTMLDivElement>(null);

  const toggleMobileMenu = () => setMobileMenuOpen(!mobileMenuOpen);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
      if (basketRef.current && !basketRef.current.contains(event.target as Node)) {
        setBasketOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Build navigation items from CMS; empty array if none configured
  const navItems = settings.navigation.map(item => ({
    label: item.label,
    href: item.url,
    isExternal: item.openInNewTab,
  }));

  return (
    <>
    <header className={styles.header}>
    <nav className={styles.nav}>
      <div className={styles.logo}>
        {config.isDemo && <span className={styles.demoBadge}>DEMO</span>}
        {settings.logo ? (
          <Link to="/" className={styles.logoText}>
            <img src={settings.logo.url} alt={settings.siteName} className={styles.logoImage} />
          </Link>
        ) : (
          <Link to="/" className={styles.logoText}>
            {settings.siteName}
          </Link>
        )}
      </div>

      <button
        className={`${styles.menuToggle} ${mobileMenuOpen ? styles.menuToggleOpen : ''}`}
        onClick={toggleMobileMenu}
        aria-label="Toggle navigation"
        aria-expanded={mobileMenuOpen}
      >
        <span className={styles.menuBar}></span>
        <span className={styles.menuBar}></span>
        <span className={styles.menuBar}></span>
      </button>

      <ul className={`${styles.navList} ${mobileMenuOpen ? styles.navListOpen : ''}`}>
        {navItems.map((item, index) => (
          <li key={index} className={styles.navItem}>
            {item.href.startsWith('/') && !item.href.includes('#') && !item.isExternal ? (
              <Link
                to={item.href}
                className={styles.navLink}
                onClick={() => setMobileMenuOpen(false)}
              >
                {item.label}
              </Link>
            ) : (
              <a
                href={item.href}
                className={styles.navLink}
                target={item.isExternal ? '_blank' : undefined}
                rel={item.isExternal ? 'noopener noreferrer' : undefined}
                onClick={() => setMobileMenuOpen(false)}
              >
                {item.label}
              </a>
            )}
          </li>
        ))}
        {settings.showReserveButton && (
          <li className={styles.navItem}>
            <Link
              to="/rezerwacje"
              className={`${styles.navLink} ${styles.reserveButton}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              {settings.reserveButtonText || t('nav.reserveOnline')}
            </Link>
          </li>
        )}
      </ul>

      <div className={styles.navActions}>
        {settings.features.shopEnabled && (
          <div className={styles.basketContainer} ref={basketRef}>
            <BasketIcon onClick={() => setBasketOpen(prev => !prev)} />
            <BasketPopup isOpen={basketOpen} onClose={() => setBasketOpen(false)} />
          </div>
        )}
        {settings.phoneButtonNumber && (
          <a
            href={`tel:${settings.phoneButtonNumber.replace(/\s/g, '')}`}
            className={`${styles.phoneButton} ${styles.phoneButtonDesktop}`}
            aria-label={settings.phoneButtonNumber}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 256 256"><path d="M222.37,158.46l-47.11-21.11-.13-.06a16,16,0,0,0-15.17,1.4,8.12,8.12,0,0,0-.75.56L143.16,155A102,102,0,0,1,101,112.84l15.77-16.05,0,0a16,16,0,0,0,1.94-15.91l-21.11-47.11A16,16,0,0,0,82.59,24a16.14,16.14,0,0,0-5.18.86L38.19,39.51a16.11,16.11,0,0,0-11,14.07c-.11,1.35-.16,2.7-.16,4.06,0,103,83.66,186.7,186.69,186.7,1.36,0,2.71,0,4.06-.16a16.11,16.11,0,0,0,14.07-11l14.65-39.22A16.17,16.17,0,0,0,222.37,158.46Z"></path></svg>
          </a>
        )}
        {settings.showUserAvatar && (
          <div className={styles.userMenu} ref={userMenuRef}>
            <button
              className={styles.userMenuTrigger}
              aria-label="Konto"
              aria-expanded={userMenuOpen}
              onClick={() => setUserMenuOpen(!userMenuOpen)}
            >
              <svg className={styles.userIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                <circle cx="12" cy="7" r="4"></circle>
              </svg>
            </button>
            {userMenuOpen && (
              <div className={styles.userMenuDropdown}>
                {isAuthenticated ? (
                  <>
                    <Link
                      to="/moje-konto"
                      className={styles.userMenuItem}
                      onClick={() => { setUserMenuOpen(false); setMobileMenuOpen(false); }}
                    >
                      {t('nav.myAccount')}
                    </Link>
                    <button
                      onClick={() => { logout(); setUserMenuOpen(false); setMobileMenuOpen(false); }}
                      className={styles.userMenuItem}
                    >
                      {t('nav.logout')}
                    </button>
                  </>
                ) : (
                  <Link
                    to="/logowanie"
                    className={styles.userMenuItem}
                    onClick={() => { setUserMenuOpen(false); setMobileMenuOpen(false); }}
                  >
                    {t('nav.login')}
                  </Link>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
    </header>
    {settings.phoneButtonNumber && (
      <a
        href={`tel:${settings.phoneButtonNumber.replace(/\s/g, '')}`}
        className={styles.phoneButtonFloating}
        aria-label={settings.phoneButtonNumber}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 256 256"><path d="M222.37,158.46l-47.11-21.11-.13-.06a16,16,0,0,0-15.17,1.4,8.12,8.12,0,0,0-.75.56L143.16,155A102,102,0,0,1,101,112.84l15.77-16.05,0,0a16,16,0,0,0,1.94-15.91l-21.11-47.11A16,16,0,0,0,82.59,24a16.14,16.14,0,0,0-5.18.86L38.19,39.51a16.11,16.11,0,0,0-11,14.07c-.11,1.35-.16,2.7-.16,4.06,0,103,83.66,186.7,186.69,186.7,1.36,0,2.71,0,4.06-.16a16.11,16.11,0,0,0,14.07-11l14.65-39.22A16.17,16.17,0,0,0,222.37,158.46Z"></path></svg>
      </a>
    )}
    </>
  );
};
